import { useEffect, useState, useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useBudgetOverviewMainDashboardQuery } from '../hooks/useBudgetQueries';
import { useGovernoratesQuery } from '@/features/region/hooks/useRegionQueries';
import { BudgetFiltersBar } from '../components/BudgetFiltersBar';
import { KpiCard } from '../components/KpiCard';
import { BudgetListPanel } from '../components/BudgetListPanel';
import type { BudgetListParams } from '../api';
import {
  FileCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  Loader2Icon,
} from '@/shared/ui/Icons';
import { PageHeader } from '@/shared/components';
import { useDefaultYear } from '@/features/settings/hooks/useDashboardSettingsQuery';

type DrillParams = {
  title: string;
  status?: number;
  late?: boolean;
  date?: string;
  governorateId?: number;
};

export function MainDashboardPage() {
  const [year, setYear] = useState<string>('');
  const [governorateId, setGovernorateId] = useState<string>('');
  const [governorateLabel, setGovernorateLabel] = useState<string>('');
  const [drillDown, setDrillDown] = useState<DrillParams | null>(null);
  const defaultYear = useDefaultYear();

  useEffect(() => {
    if (!year && defaultYear) setYear(defaultYear);
  }, [defaultYear]);

  const handleGovernorateChange = (id: string, label: string) => {
    setGovernorateId(id);
    setGovernorateLabel(label);
  };

  const { data: overview, isLoading: overviewLoading } = useBudgetOverviewMainDashboardQuery({
    year: year || '',
    governorateId: governorateId || undefined,
  });
  const { data: governorates = [] } = useGovernoratesQuery();

  const submissionOverTimes = overview?.submissionOverTimes ?? [];
  const budgetsByGovernorate = overview?.budgetsByGovernorate ?? [];

  const governorateNameToId = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of governorates) map.set(g.name, String(g.id));
    return map;
  }, [governorates]);

  const totalSubmissions = useMemo(
    () => submissionOverTimes.reduce((sum, r) => sum + r.count, 0),
    [submissionOverTimes]
  );

  /** Parse d/m/yyyy or dd/m/yyyy to Date for sorting */
  const parseDmy = (dateStr: string): Date => {
    const [d, m, y] = dateStr.split('/').map(Number);
    return new Date(y, m - 1, d);
  };

  const lineChartData = useMemo(
    () =>
      [...submissionOverTimes]
        .sort((a, b) => parseDmy(a.date).getTime() - parseDmy(b.date).getTime())
        .map((r) => ({
          date: r.date,
          label: parseDmy(r.date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: '2-digit',
          }),
          submissions: r.count,
          pct: totalSubmissions ? (r.count / totalSubmissions) * 100 : 0,
        })),
    [submissionOverTimes, totalSubmissions]
  );

  const kpisLoading = overviewLoading;
  const isLoading = overviewLoading;

  const listParams = useMemo((): BudgetListParams => {
    const params: BudgetListParams = {};
    if (year) params.year = year;
    const effGovId = drillDown?.governorateId ?? (governorateId ? governorateId : undefined);
    if (effGovId != null) params.governorateId = String(effGovId);
    if (drillDown?.status !== undefined) params.status = drillDown.status;
    if (drillDown?.late !== undefined) params.late = drillDown.late;
    if (drillDown?.date) params.date = drillDown.date;
    return params;
  }, [year, governorateId, drillDown]);

  return (
    <div className="space-y-8">
      <PageHeader
        title="Budget Submission Overview"
        description="Monitor budget submissions, compliance status, and regional breakdown"
      />

      <BudgetFiltersBar
        year={year}
        onYearChange={setYear}
        governorateId={governorateId}
        onGovernorateIdChange={handleGovernorateChange}
        governorateLabel={governorateLabel}
      />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Budgets Submitted"
          value={kpisLoading || !year ? '—' : (overview?.budgetSubmitted ?? 0).toLocaleString()}
          icon={FileCheckIcon}
          accent="indigo"
        />
        <KpiCard
          label="Late Budgets"
          sublabel={year ? `On or after 3 Feb ${parseInt(year) + 1}` : 'Select a year'}
          value={kpisLoading || !year ? '—' : (overview?.lateBudgets ?? 0).toLocaleString()}
          icon={ClockIcon}
          accent="amber"
        />
        <KpiCard
          label="Compliant"
          sublabel="Status = 0"
          value={kpisLoading || !year ? '—' : (overview?.compliant ?? 0).toLocaleString()}
          icon={CheckCircleIcon}
          accent="emerald"
        />
        <KpiCard
          label="Non-Compliant"
          sublabel="Status = 2"
          value={kpisLoading || !year ? '—' : (overview?.nonCompliant ?? 0).toLocaleString()}
          icon={XCircleIcon}
          accent="rose"
        />
      </div>

      {/* Donut charts */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="overflow-hidden rounded-2xl border border-slate-200/60 bg-white p-6 shadow-soft">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Compliance Split</h3>
            <p className="mt-0.5 text-sm text-slate-500">Compliant vs Non-Compliant share</p>
          </div>
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2Icon className="h-8 w-8 animate-spin text-slate-300" />
            </div>
          ) : (overview?.compliant ?? 0) + (overview?.nonCompliant ?? 0) === 0 ? (
            <div className="flex h-64 items-center justify-center rounded-xl border-2 border-dashed border-slate-200 text-slate-400">
              No data to display
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Compliant', value: overview?.compliant ?? 0, color: '#10b981' },
                      { name: 'Non-Compliant', value: overview?.nonCompliant ?? 0, color: '#f43f5e' },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    onClick={(data: { name: string }) => {
                      if (data.name === 'Compliant') setDrillDown({ title: 'Compliant Budgets', status: 0 });
                      else setDrillDown({ title: 'Non-Compliant Budgets', status: 2 });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {[
                      { name: 'Compliant', color: '#10b981' },
                      { name: 'Non-Compliant', color: '#f43f5e' },
                    ].map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v: number, _name: string, props: { payload?: { name: string }; value?: number }) => {
                      const total = (overview?.compliant ?? 0) + (overview?.nonCompliant ?? 0);
                      const val = props?.value ?? v;
                      const pct = total ? ((val / total) * 100).toFixed(1) : '0';
                      return [`${v.toLocaleString()} (${pct}%)`, props?.payload?.name ?? ''];
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="overflow-hidden rounded-2xl border border-slate-200/60 bg-white p-6 shadow-soft">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Late vs On-Time</h3>
            <p className="mt-0.5 text-sm text-slate-500">Budgets submitted by deadline</p>
          </div>
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2Icon className="h-8 w-8 animate-spin text-slate-300" />
            </div>
          ) : (overview?.budgetSubmitted ?? 0) === 0 ? (
            <div className="flex h-64 items-center justify-center rounded-xl border-2 border-dashed border-slate-200 text-slate-400">
              No data to display
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: 'On-Time',
                        value: Math.max(0, (overview?.budgetSubmitted ?? 0) - (overview?.lateBudgets ?? 0)),
                        color: '#10b981',
                      },
                      { name: 'Late', value: overview?.lateBudgets ?? 0, color: '#f59e0b' },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    onClick={(data: { name: string }) => {
                      if (data.name === 'On-Time') setDrillDown({ title: 'On-Time Budgets', late: false });
                      else setDrillDown({ title: 'Late Budgets', late: true });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {[
                      { name: 'On-Time', color: '#10b981' },
                      { name: 'Late', color: '#f59e0b' },
                    ].map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v: number, _name: string, props: { payload?: { name: string }; value?: number }) => {
                      const total = overview?.budgetSubmitted ?? 0;
                      const val = props?.value ?? v;
                      const pct = total ? ((val / total) * 100).toFixed(1) : '0';
                      return [`${v.toLocaleString()} (${pct}%)`, props?.payload?.name ?? ''];
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Line chart and governorate bar */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200/60 bg-white p-4 shadow-soft">
          <div className="mb-4 shrink-0">
            <h3 className="text-lg font-semibold text-slate-900">Submissions Over Time</h3>
            <p className="mt-0.5 text-sm text-slate-500">Number of submissions per day</p>
          </div>
          {isLoading ? (
            <div className="flex min-h-[320px] flex-1 items-center justify-center">
              <Loader2Icon className="h-8 w-8 animate-spin text-slate-300" />
            </div>
          ) : lineChartData.length === 0 ? (
            <div className="flex min-h-[320px] flex-1 items-center justify-center rounded-xl border-2 border-dashed border-slate-200 text-slate-400">
              No data to display
            </div>
          ) : (
            <div className="h-[320px] min-h-0 flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineChartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => v.toLocaleString()} />
                  <Tooltip
                    formatter={(v: number, _name: string, props: { payload?: { pct?: number } }) => {
                      const pct = props?.payload?.pct ?? 0;
                      return [`${v.toLocaleString()} (${pct.toFixed(1)}%)`, 'Submissions'];
                    }}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.date
                        ? new Date(payload[0].payload.date + 'T00:00:00').toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : ''
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="submissions"
                    stroke="#6366f1"
                    strokeWidth={2}
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      const dateStr = payload?.date;
                      if (!dateStr) return <circle cx={props.cx} cy={props.cy} r={5} fill="#6366f1" />;
                      const handleClick = (e: React.MouseEvent) => {
                        e.stopPropagation();
                        setDrillDown({
                          title: `Budgets submitted on ${new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`,
                          date: dateStr,
                        });
                      };
                      return (
                        <g
                          onClick={handleClick}
                          className="cursor-pointer"
                          style={{ pointerEvents: 'all' }}
                          role="button"
                          aria-label={`View budgets for ${dateStr}`}
                        >
                          <circle cx={cx} cy={cy} r={8} fill="transparent" />
                          <circle cx={cx} cy={cy} r={5} fill="#6366f1" className="hover:opacity-80" />
                        </g>
                      );
                    }}
                    activeDot={(props: { cx?: number; cy?: number; payload?: { date?: string } }) => {
                      const { cx, cy, payload } = props;
                      const dateStr = payload?.date;
                      if (!dateStr) return <circle cx={cx} cy={cy} r={6} fill="#6366f1" />;
                      const handleClick = (e: React.MouseEvent) => {
                        e.stopPropagation();
                        setDrillDown({
                          title: `Budgets submitted on ${new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`,
                          date: dateStr,
                        });
                      };
                      return (
                        <g onClick={handleClick} className="cursor-pointer" style={{ pointerEvents: 'all' }} role="button">
                          <circle cx={cx} cy={cy} r={8} fill="transparent" />
                          <circle cx={cx} cy={cy} r={6} fill="#6366f1" stroke="#4338ca" strokeWidth={2} />
                        </g>
                      );
                    }}
                    name="Submissions"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200/60 bg-white p-4 shadow-soft">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Budgets by Governorate</h3>
            <p className="mt-0.5 text-sm text-slate-500">Number of submissions per governorate</p>
          </div>
          {isLoading ? (
            <div className="flex min-h-[320px] items-center justify-center">
              <Loader2Icon className="h-8 w-8 animate-spin text-slate-300" />
            </div>
          ) : budgetsByGovernorate.length === 0 ? (
            <div className="flex min-h-[320px] items-center justify-center rounded-xl border-2 border-dashed border-slate-200 text-slate-400">
              No data to display
            </div>
          ) : (
            <div className="h-[320px] min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart
                  data={budgetsByGovernorate.map((row) => {
                    const total = budgetsByGovernorate.reduce((s, r) => s + r.count, 0);
                    return {
                      governorate: row.date,
                      governorateId: governorateNameToId.get(row.date) ?? null,
                      count: row.count,
                      pct: total ? (row.count / total) * 100 : 0,
                    };
                  })}
                  margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="governorate" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => v.toLocaleString()} />
                  <Tooltip
                    formatter={(v: number, _name: string, props: { payload?: { pct?: number } }) => {
                      const pct = props?.payload?.pct ?? 0;
                      return [`${v.toLocaleString()} (${pct.toFixed(1)}%)`, 'Submissions'];
                    }}
                  />
                  <Bar
                    dataKey="count"
                    fill="#6366f1"
                    name="Submissions"
                    radius={[2, 2, 0, 0]}
                    onClick={(data: { governorateId?: string | null; governorate: string }) => {
                      const id = data.governorateId;
                      if (id != null)
                        setDrillDown({
                          title: data.governorate,
                          governorateId: parseInt(id, 10),
                        });
                    }}
                    cursor="pointer"
                  />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <BudgetListPanel
        selectionTitle={drillDown?.title ?? null}
        listParams={listParams}
        onClearSelection={() => setDrillDown(null)}
      />
    </div>
  );
}
